import { useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import { LABEL_LANGUAGES, isLabelLanguage, type LabelLanguage } from "@hisense/shared";

const STORAGE_KEY = "hisense.labels.language";

/**
 * Ngôn ngữ in trên tem — chọn LÚC IN (Roary 12/10/2026), không phải cờ trên sản phẩm: kho sản phẩm dùng chung hai công ty, đội Việt
 * Nam cần tem thuần tiếng Anh, đội Trung Quốc cần tiếng Hoa hoặc cả hai. Cùng nguyên tắc với ngôn ngữ in của chứng từ
 * (`PrintLanguageToggle`): thứ tiếng thuộc về lần in / người nhận, không thuộc về mã hàng.
 *
 * Nhớ lựa chọn trên máy (tiện cho người in, không phải dữ liệu); lần đầu là tiếng Anh — đúng thứ tem vẫn in trước nay.
 */
const listeners = new Set<() => void>();
let current: LabelLanguage | null = null;

function readLanguage(): LabelLanguage {
  if (current) return current;
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    current = isLabelLanguage(saved) ? saved : "en";
  } catch {
    current = "en";
  }
  return current;
}

function writeLanguage(next: LabelLanguage) {
  current = next;
  try { localStorage.setItem(STORAGE_KEY, next); } catch { /* Chỉ là tiện ích; không lưu được thì vẫn dùng được trong phiên này. */ }
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** Một giá trị chung cho mọi hộp in tem: danh sách sản phẩm có một nút in tem mỗi dòng, đổi ở dòng này thì dòng kia phải theo. */
export function useLabelLanguage(): [LabelLanguage, (next: LabelLanguage) => void] {
  const language = useSyncExternalStore(subscribe, readLanguage, () => "en" as const);
  return [language, writeLanguage];
}

export function LabelLanguageToggle({ value, onChange }: { value: LabelLanguage; onChange: (next: LabelLanguage) => void }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-ink-soft">{t("common.labelLanguage")}</span>
        <div className="inline-flex overflow-hidden rounded-md border border-line" role="group" aria-label={t("common.labelLanguage")}>
          {LABEL_LANGUAGES.map((lang) => (
            <button
              key={lang}
              type="button"
              aria-pressed={value === lang}
              className={`px-3 py-1.5 text-sm ${value === lang ? "bg-brand text-white" : "bg-surface text-ink-soft hover:bg-surface-2"}`}
              onClick={() => onChange(lang)}
            >
              {t(`common.labelLanguage_${lang}`)}
            </button>
          ))}
        </div>
      </div>
      <p className="text-xs text-ink-faint">{t("common.labelLanguageHint")}</p>
    </div>
  );
}
